import { UI } from '../ui/status.js';
import { ensureDb, withDb, safeInterval, clearAllIntervals, isTransientIdbError } from '../data/idb.js';
import { OFFLINE_MODE_ENABLED, SYNC_EVERY_MIN, isAuthenticated, redirectToAuth } from '../core/config.js';
import { Sync } from '../sync/index.js';
import { serverFetchAllBadges } from '../data/storage.js';
import { lwDispatch } from '../core/livewire.js';
import { attachUiListeners } from '../ui/listeners.js';
import { initSimulator } from '../simulator/index.js';

export const State = Object.freeze({
    BOOTING: 'BOOTING',
    AUTH_REQUIRED: 'AUTH_REQUIRED',
    LOADING_EVENT: 'LOADING_EVENT',
    READY: 'READY'
});

export const App = {
    state: State.BOOTING,
    db: null,
    lastRemoteSyncAt: 0,

    async boot() {

        if (!isAuthenticated()) {
            this.state = State.AUTH_REQUIRED;
            redirectToAuth();
            return;
        }

        if (!EVENT_ID) {
            console.warn('[checkin] Missing EVENT_ID');
            return;
        }

        // IDB init only if offline mode enabled
        if (OFFLINE_MODE_ENABLED) {
            try {
                await ensureDb();
            } catch (e) {
                console.warn('[checkin] IDB unavailable; operating online-only when possible.', e);
            }
        }

        navigator.onLine ? UI.setOnline() : UI.setOffline();

        const is_simulator = (window.checkin_type === 'TEST_CONTROL') || (window.checkin_type === 'TEST_COUNTING');

        window.addEventListener('online', () => {
            UI.setOnline();
            this.tryBackgroundSync(true);
        });

        window.addEventListener('offline', () => {
            UI.setOffline();
            this.tryBackgroundSync(false);
        });

        if (OFFLINE_MODE_ENABLED) lwDispatch('checkin.badges.initial.fetch');

        await this.renderSyncStatus();
        safeInterval(() => this.renderSyncStatus(), 5000);
        safeInterval(() => this.tryBackgroundSync(), 30000);

        window.addEventListener('pagehide', clearAllIntervals, { once: true });
        window.addEventListener('beforeunload', clearAllIntervals, { once: true });

        attachUiListeners(window.checkin_type, is_simulator);

        if (is_simulator) {
            initSimulator();
        }

        this.state = State.READY;
        document.body.classList.remove('fetching');
        console.info('[checkin] READY ✓');
    },

    shouldSyncNow() {
        if (!navigator.onLine) return false;
        return (Date.now() - this.lastRemoteSyncAt) > (SYNC_EVERY_MIN * 60000);
    },

    async tryBackgroundSync(becauseOnlineEvent = false) {
        // Skip background sync entirely if offline mode disabled (no local store to sync)
        if (!OFFLINE_MODE_ENABLED) return;

        try {
            if (becauseOnlineEvent || this.shouldSyncNow()) {
                await withDb(async (db) => {
                    await Sync.pushUnsynced(db);
                    await Sync.pullDelta(db);
                });
                this.lastRemoteSyncAt = Date.now();
                await this.renderSyncStatus();
            }
        } catch (err) {
            if (!isTransientIdbError(err)) console.error(err);
        }
    },

    async renderSyncStatus() {
        // Only show sync status when offline mode is enabled
        if (!OFFLINE_MODE_ENABLED) return;

        // Try local first
        try {
            const badges = await withDb(db => db.getAll('badges'));
            if (Array.isArray(badges)) {
                UI.renderSyncSummary(badges.length, badges.filter(b => b.synced).length);
                return; // ✅ No server call if local worked
            }
        } catch (e) {
            console.warn('[checkin] renderSyncStatus: local fetch failed, falling back to remote', e);
        }

        // Fallback: only if online
        if (navigator.onLine) {
            try {
                const remote = await serverFetchAllBadges();
                UI.renderSyncSummary(remote.length, remote.length);
            } catch {
                UI.renderSyncSummary(0, 0);
            }
        } else {
            UI.renderSyncSummary(0, 0);
        }
    },

};
